import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { checkDetailTicket } from '@/service/ticketService';
import { multiPanelAtom } from '@/state/atom/panelAtom';
import {
    setPanelSelector,
    triggerReloadTicketListByPanelType
} from '@/state/selector/panelSelector';
import { Tooltip, Typography } from '@material-ui/core';
import S3Image from '@/component/common/S3Image';
import { useRecoilState, useSetRecoilState } from 'recoil';
import trI18n from '@/common/utils/trI18n';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import useListTicketOnDemandDisplay from '@/component/multiLanguage/hooks/useListTicketOnDemandDisplay';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';

export default function DetailTicketListLastMessage(props) {
    // Re-render when the translation dictionary loads/extends so tr() text updates.
    useTranslationSubscription();

    const { ticket, setIsLoading, API, getTicketListType, ticketListType, panelType, viewSelectedId } = props;

    // On-demand list translation (MLT-001-F4): resolve the last message to display.
    // Title is handled in DetailTicketList, so only the last message is resolved here.
    const { displayLastMessage } = useListTicketOnDemandDisplay(
        null,
        ticket?.lastMessageInfo?.message,
        { panelType, viewKey: viewSelectedId, ticketId: ticket.id }
    );

    // Both email ticket types (EMAIL and EMAIL_REPLY) store the last message as HTML
    // (same as the email chat content), so strip the markup to the plain text that is
    // shown in the list and used verbatim as the overflow tooltip value. Non-email
    // tickets keep the raw message. Keep this list in sync with the email-type handling
    // elsewhere (DetailContentOnDemandHandler.EMAIL_TICKET_TYPES / Chat email rendering).
    // Fallback to '' when there is no last message so cleanHtml/unEscapeHtml always
    // receive a string (a record without a last message resolves to null/undefined).
    const lastMessageText = displayLastMessage || '';
    const messageDisplay = [Constant.TICKET_TYPE.EMAIL, Constant.TICKET_TYPE.EMAIL_REPLY].includes(
        ticket?.ticketType
    )
        ? Utils.unEscapeHtml(Utils.cleanHtml(lastMessageText))
        : lastMessageText;

    const addNewPanel = useSetRecoilState(setPanelSelector);
    const reloadTicketListByPanelType = useSetRecoilState(triggerReloadTicketListByPanelType);
    const [panelList] = useRecoilState(multiPanelAtom);

    const openChatThreadPanel = async (ticketDetail, selectThreadId = null) => {
        // console.log('177259 done openChatThreadPanel', props)
        setIsLoading(true);
        let {status: currentStatusTicket} = await checkDetailTicket(API, ticketDetail.id);
        setIsLoading(false);

        if (currentStatusTicket === Constant.TICKET_DELETED_STATUS.REMOVED) {
            reloadTicketListByPanelType({
                panelType: getTicketListType()
                    ? getTicketListType()
                    : Constant.PANEL_TYPE.CUSTOMER_TICKET_LIST
            });
            return;
        }

        let newPanelChatId = Date.now().toString();
        if (ticketListType.includes('helpdesk')) {
            let helpdeskChatThreadPanel = panelList.find(
                p => p.type === Constant.PANEL_TYPE.HELPDESK_CHATTHREAD
            );
            if (!Utils.isEmptyObject(helpdeskChatThreadPanel)) {
                newPanelChatId = helpdeskChatThreadPanel.id;
            }
        } else {
            let customerChatThreadPanel = panelList.find(
                p => p.type === Constant.PANEL_TYPE.CUSTOMER_CHATTHREAD
            );
            if (!Utils.isEmptyObject(customerChatThreadPanel)) {
                newPanelChatId = customerChatThreadPanel.id;
            }
        }

        const title = Utils.getTitle(ticketDetail.settingTitle);
        const panelType =
            ticketListType === 'helpdesk'
                ? Constant.PANEL_TYPE.HELPDESK_CHATTHREAD
                : Constant.PANEL_TYPE.CUSTOMER_CHATTHREAD;
        let openDetailPanelProps = {
            API: props.API,
            panelId: props?.panelId,
            tabId: props?.tabId,
            ticketId: ticketDetail.id,
            ticketListType: ticketListType,
            panelType: panelType,
            history: props?.history
        };
        addNewPanel({
            //update
            type: panelType,
            tabId: ticketDetail.id,
            props: openDetailPanelProps,
            ticketId: ticketDetail.id,
            ticketOutline: title,
            selectThreadId: selectThreadId ? selectThreadId : '',
            isShowAllChatPost: false,
            newPanelId: newPanelChatId,
            openFromType: 'lastMessage',
            startRelatePanelFromPanelId: props?.panelId,
            isEmailTicket: ticketDetail.ticketType === Constant.TICKET_TYPE.EMAIL
        });

        // open ticket detail panel
        let isDraft = ticketDetail.public === Constant.TICKET_PUBLIC.DRAFT;
        setTimeout(async () => {
            await addNewPanel({
                //update
                type: props.ticketListType.includes('helpdesk')
                    ? Constant.PANEL_TYPE.HELPDESK_TICKET_DETAIL
                    : Constant.PANEL_TYPE.CUSTOMER_TICKET_DETAIL,
                tabId: ticketDetail.id,
                props: openDetailPanelProps,
                ticketId: ticketDetail.id,
                ticketOutline: title,
                isDraft: isDraft,
                currentPanelId: newPanelChatId,
                openRelatePanelType: panelType,
                isEmailTicket: ticketDetail.ticketType === Constant.TICKET_TYPE.EMAIL
            });
        }, 1500);
    };
    return (
        <div className="ticket-chat-notice">
            <div className="chat-avatar">
                {Utils.isEmptyString(ticket.lastMessageInfo.shortFullName) && (
                    <Tooltip
                        title={
                            ticket.lastMessage?.userId?.includes('anonymous')
                                ? tr('未登録カスタマー')
                                : ticket.lastMessageInfo.fullName
                        }
                    >
                        {ticket.lastMessageInfo.avatar === Constant.AVATAR_DEFAULT ? (
                            <img
                                alt=""
                                src={ticket.lastMessageInfo.avatar}
                                className="chat-avatar-frame"
                            />
                        ) : (
                            <div>
                                <S3Image
                                    title={ticket.lastMessageInfo.fullName}
                                    imgKey={ticket.lastMessageInfo.avatar}
                                    className="chat-avatar-frame"
                                />
                            </div>
                        )}
                    </Tooltip>
                )}
                {!Utils.isEmptyString(ticket.lastMessageInfo.shortFullName) && (
                    <Tooltip title={ticket.lastMessageInfo.fullName}>
                        <Typography
                            component="div"
                            variant="subtitle2"
                            className="chat-avatar-frame"
                        >
                            {ticket.lastMessageInfo.shortFullName}
                        </Typography>
                    </Tooltip>
                )}
                {ticket.lastMessageInfo.unreadMessageNumber > 0 && (
                    <span className="msg-unread">
                        {ticket.lastMessageInfo.unreadMessageNumber > 99
                            ? '99+'
                            : ticket.lastMessageInfo.unreadMessageNumber}
                    </span>
                )}
            </div>
            <OverflowTooltipContent
                isShowTooltip={false}
                component="div"
                className="message"
                text={messageDisplay}
                style={{ flex: 1, minWidth: 0, width: 'auto' }}
                onClick={() => openChatThreadPanel(ticket, ticket.lastMessageInfo.chatThreadId)}
            />
            {{...ticket}?.hasUnsentMessage && (
                <div className="chat-avatar">
                    <Tooltip title={trI18n('UN-SENT-MESSAGE-EXIST')} placement="top-center">
                        <img
                            alt=""
                            src={'/assets/image/exclamation-point.svg'}
                            className="chat-avatar-frame"
                        />
                    </Tooltip>
                </div>
            )}
        </div>
    );
}
